import React, { useEffect, useMemo, useState } from "react";
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Grid,
    InputAdornment,
    MenuItem,
    Stack,
    TextField,
    Typography,
} from "@mui/material";
import axios from "axios";
import { API_URL } from "./config";
import { motion } from "framer-motion";
import { CreditCard, Category, Payments, PhoneIphone } from "@mui/icons-material";
import { AdminPageShell, AdminStat, AdminSurface } from "./admin/AdminPageShell";

const fieldStyles = {
    '& .MuiOutlinedInput-root': {
        borderRadius: '18px',
        bgcolor: '#fff',
    },
};

const ChangePlan = () => {
    const [packages, setPackages] = useState([]);
    const [formData, setFormData] = useState({ package: "" });
    const [paymentData, setPaymentData] = useState({
        mpesanumber: "",
        paymentMethod: "Mpesa",
        amount: "",
        package: "",
    });
    const [loading, setLoading] = useState(false);
    const [paymentLoading, setPaymentLoading] = useState(false);
    const [successMessage, setSuccessMessage] = useState("");
    const [errorMessage, setErrorMessage] = useState("");
    const [showPaymentDetails, setShowPaymentDetails] = useState(false);
    const [selectedPackage, setSelectedPackage] = useState(null);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const response = await axios.get(API_URL + "packages.php", { withCredentials: true });
                if (response.data.success) {
                    setPackages(response.data.packages);
                } else {
                    setErrorMessage(response.data.message || "Failed to fetch data.");
                }
            } catch (error) {
                setErrorMessage("Error fetching data. Please try again.");
            }
        };

        fetchData();
    }, []);

    const packageCountLabel = useMemo(() => packages.length, [packages]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handlePaymentChange = (e) => {
        const { name, value } = e.target;
        setPaymentData((prev) => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setSuccessMessage("");
        setErrorMessage("");

        try {
            const response = await axios.post(API_URL + "packages.php", formData, {
                withCredentials: true,
            });

            if (response.data.success) {
                setSuccessMessage("Package changed successfully. Please add payment details.");
                const selected = packages.find((pkg) => pkg.id === formData.package);

                if (selected) {
                    setSelectedPackage(selected);
                    setPaymentData((prev) => ({
                        ...prev,
                        package: selected.id,
                        amount: selected.amount,
                    }));
                }

                setShowPaymentDetails(true);
                setFormData({ package: "" });
            } else {
                setErrorMessage(response.data.message);
            }
        } catch (error) {
            setErrorMessage("Error Changing Package. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    const handlePaymentSubmit = async (e) => {
        e.preventDefault();
        setPaymentLoading(true);
        setSuccessMessage("");
        setErrorMessage("");

        try {
            const response = await axios.post(API_URL + "process-payment.php", paymentData, {
                withCredentials: true,
            });

            if (response.data.success) {
                setSuccessMessage("Package changed and payment recorded successfully!");
                setShowPaymentDetails(false);
                setSelectedPackage(null);
                setPaymentData({
                    mpesanumber: "",
                    paymentMethod: "Mpesa",
                    amount: "",
                    package: "",
                });
            } else {
                setErrorMessage(response.data.message);
            }
        } catch (error) {
            setErrorMessage("Error saving payment details. Please try again.");
        } finally {
            setPaymentLoading(false);
        }
    };

    return (
        <AdminPageShell
            icon={<Payments />}
            eyebrow="Subscription management"
            title="Change Plan"
            description="Upgrade or switch packages in a guided flow with a clearer package review and payment confirmation step."
            maxWidth="lg"
        >
            <Box
                sx={{
                    display: "grid",
                    gap: 3,
                    gridTemplateColumns: { xs: "minmax(0, 1fr)", md: "minmax(240px, 320px) minmax(0, 1fr)" },
                    alignItems: "start",
                    width: "100%",
                }}
            >
                <Box sx={{ minWidth: 0 }}>
                    <Stack spacing={2}>
                        <AdminStat label="Packages available" value={packageCountLabel} helper="Every package loaded from the backend appears here." />
                        <AdminSurface>
                            <Typography variant="h6" sx={{ fontWeight: 700, color: '#2d2a5a', mb: 1.25 }}>
                                How this works
                            </Typography>
                            <Stack spacing={1.1}>
                                <Typography variant="body2" color="text.secondary">1. Pick the package you want to activate.</Typography>
                                <Typography variant="body2" color="text.secondary">2. Review the package amount in the payment step.</Typography>
                                <Typography variant="body2" color="text.secondary">3. Record the Mpesa payment details to finish the change.</Typography>
                            </Stack>
                        </AdminSurface>
                    </Stack>
                </Box>
                <Box sx={{ minWidth: 0 }}>
                    <AdminSurface>
                        <Stack spacing={3}>
                            <Box>
                                <Typography variant="h5" sx={{ fontWeight: 800, color: '#2d2a5a' }}>
                                    {showPaymentDetails ? 'Payment details' : 'Select a package'}
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
                                    {showPaymentDetails
                                        ? 'Confirm the payment information for the selected package.'
                                        : 'Start by choosing the package you want to move to.'}
                                </Typography>
                            </Box>

                            {!showPaymentDetails ? (
                                <Box component="form" onSubmit={handleSubmit}>
                                    <TextField
                                        label="Select package"
                                        name="package"
                                        value={formData.package}
                                        onChange={handleInputChange}
                                        select
                                        fullWidth
                                        required
                                        sx={fieldStyles}
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <Category color="primary" />
                                                </InputAdornment>
                                            ),
                                        }}
                                    >
                                        {packages.map((pkg) => (
                                            <MenuItem key={pkg.id} value={pkg.id} sx={{ py: 1.5 }}>
                                                {pkg.name} - Kes {pkg.amount}
                                            </MenuItem>
                                        ))}
                                    </TextField>

                                    <motion.div whileHover={{ scale: 1.01 }} whileTap={{ scale: 0.99 }}>
                                        <Button
                                            type="submit"
                                            variant="contained"
                                            fullWidth
                                            size="large"
                                            disabled={loading}
                                            sx={{
                                                mt: 3,
                                                py: 1.6,
                                                borderRadius: '18px',
                                                fontWeight: 700,
                                                fontSize: '1rem',
                                                textTransform: 'none',
                                                background:
                                                    'linear-gradient(135deg, #4a90e2 0%, #6a5af9 55%, #9013fe 100%)',
                                                boxShadow: '0 18px 30px rgba(106, 90, 249, 0.24)',
                                            }}
                                        >
                                            {loading ? <CircularProgress size={24} sx={{ color: 'white' }} /> : 'Continue to Payment'}
                                        </Button>
                                    </motion.div>
                                </Box>
                            ) : (
                                <Box component="form" onSubmit={handlePaymentSubmit}>
                                    <AdminSurface
                                        sx={{
                                            mb: 2.5,
                                            p: 2.5,
                                            background: 'linear-gradient(180deg, rgba(243,244,255,0.9) 0%, rgba(255,255,255,0.96) 100%)',
                                        }}
                                    >
                                        <Typography variant="subtitle2" sx={{ color: 'text.secondary', mb: 1 }}>
                                            Selected package
                                        </Typography>
                                        <Typography variant="h6" sx={{ fontWeight: 800, color: '#2d2a5a' }}>
                                            {selectedPackage?.name || 'Package not available'}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
                                            Amount due: Kes {selectedPackage?.amount ?? paymentData.amount}
                                        </Typography>
                                    </AdminSurface>

                                    <Grid container spacing={2}>
                                        <Grid item xs={12} md={6}>
                                            <TextField
                                                label="Mpesa number"
                                                name="mpesanumber"
                                                value={paymentData.mpesanumber}
                                                onChange={handlePaymentChange}
                                                fullWidth
                                                required
                                                type="number"
                                                sx={fieldStyles}
                                                InputProps={{
                                                    startAdornment: (
                                                        <InputAdornment position="start">
                                                            <PhoneIphone color="primary" />
                                                        </InputAdornment>
                                                    ),
                                                }}
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={6}>
                                            <TextField
                                                label="Payment method"
                                                name="paymentMethod"
                                                value={paymentData.paymentMethod}
                                                onChange={handlePaymentChange}
                                                select
                                                fullWidth
                                                required
                                                sx={fieldStyles}
                                                InputProps={{
                                                    startAdornment: (
                                                        <InputAdornment position="start">
                                                            <CreditCard color="primary" />
                                                        </InputAdornment>
                                                    ),
                                                }}
                                            >
                                                <MenuItem value="Mpesa">Mpesa</MenuItem>
                                            </TextField>
                                        </Grid>
                                        <Grid item xs={12}>
                                            <TextField
                                                label="Amount"
                                                name="amount"
                                                type="number"
                                                value={paymentData.amount}
                                                disabled
                                                onChange={handlePaymentChange}
                                                fullWidth
                                                required
                                                sx={fieldStyles}
                                                InputProps={{
                                                    startAdornment: (
                                                        <InputAdornment position="start">
                                                            <Payments color="primary" />
                                                        </InputAdornment>
                                                    ),
                                                }}
                                            />
                                        </Grid>
                                    </Grid>

                                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mt: 3 }}>
                                        <Button
                                            variant="outlined"
                                            fullWidth
                                            onClick={() => setShowPaymentDetails(false)}
                                            sx={{ py: 1.4, borderRadius: '18px', textTransform: 'none', fontWeight: 700 }}
                                        >
                                            Back
                                        </Button>
                                        <motion.div style={{ width: '100%' }} whileHover={{ scale: 1.01 }} whileTap={{ scale: 0.99 }}>
                                            <Button
                                                type="submit"
                                                variant="contained"
                                                fullWidth
                                                disabled={paymentLoading}
                                                sx={{
                                                    py: 1.4,
                                                    borderRadius: '18px',
                                                    textTransform: 'none',
                                                    fontWeight: 700,
                                                    background:
                                                        'linear-gradient(135deg, #4a90e2 0%, #6a5af9 55%, #9013fe 100%)',
                                                    boxShadow: '0 18px 30px rgba(106, 90, 249, 0.24)',
                                                }}
                                            >
                                                {paymentLoading ? <CircularProgress size={24} sx={{ color: 'white' }} /> : 'Record Payment'}
                                            </Button>
                                        </motion.div>
                                    </Stack>
                                </Box>
                            )}

                            {(successMessage || errorMessage) && (
                                <Stack spacing={1.5}>
                                    {successMessage && (
                                        <Alert severity="success" onClose={() => setSuccessMessage("")}>
                                            {successMessage}
                                        </Alert>
                                    )}
                                    {errorMessage && (
                                        <Alert severity="error" onClose={() => setErrorMessage("")}>
                                            {errorMessage}
                                        </Alert>
                                    )}
                                </Stack>
                            )}
                        </Stack>
                    </AdminSurface>
                </Box>
            </Box>
        </AdminPageShell>
    );
};

export default ChangePlan;
